class FreeShippingProgressManager {
  constructor() {
    if (window.FSPManagerInstance) return window.FSPManagerInstance;
    window.FSPManagerInstance = this;
    
    this.instances = [];
    this.interceptAjax();
    
    document.addEventListener('cart:updated', (evt) => {
      if (evt.detail && evt.detail.cart) {
        this.updateAll(evt.detail.cart.total_price);
      }
    });
  }

  register(instance) {
    if (!this.instances.includes(instance)) {
      this.instances.push(instance);
    }
  }

  unregister(instance) {
    this.instances = this.instances.filter(i => i !== instance);
  }

  updateAll(cartTotal) {
    this.instances.forEach(instance => instance.updateProgress(cartTotal));
  }

  interceptAjax() {
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      
      try {
        const url = (args[0] instanceof Request ? args[0].url : args[0]) || '';
        
        if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/clear') || url.includes('/cart/update') || url.includes('cart.js')) {
          
          const resClone = response.clone();
          const data = await resClone.json();
          
          if (data && data.total_price !== undefined) {
            self.updateAll(data.total_price);
          } else if (!url.includes('cart.js')) {
            fetch((window.Shopify?.routes?.root || '/') + 'cart.js')
              .then(r => r.json())
              .then(cart => {
                self.updateAll(cart.total_price);
              });
          }
        }
      } catch (e) {}
      
      return response;
    };
  }
}

const fspManager = new FreeShippingProgressManager();

class FreeShippingProgress extends HTMLElement {
  constructor() {
    super();
    this.wrapper = this.querySelector('.fsp-wrapper');
    this.settings = JSON.parse(this.querySelector('[data-fsp-settings]')?.textContent || '{}');
    this.milestones = JSON.parse(this.querySelector('[data-fsp-milestones]')?.textContent || '[]').sort((a, b) => a.threshold - b.threshold);
    
    this.progressBar = this.querySelector('.fsp-progress-bar');
    this.messageEl = this.querySelector('.fsp-message');
    this.markersContainer = this.querySelector('.fsp-milestones');
    
    this.moneyFormat = this.settings.moneyFormat || '${{amount}}';
    
    if (this.milestones.length > 0) {
      this.init();
    } else {
      this.style.display = 'none';
    }
  }

  connectedCallback() {
    fspManager.register(this);
  }

  disconnectedCallback() {
    fspManager.unregister(this);
  }

  init() {
    this.renderMarkers();
    this.updateProgress(this.settings.currentCartTotal || 0);
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, this.moneyFormat);
    }
    const val = (cents / 100).toFixed(2);
    return this.moneyFormat.replace('{{amount}}', val).replace('{{amount_no_decimals}}', Math.floor(val));
  }
  
  parseMessage(template, remaining, current, threshold, rewardName) {
    return template
      .replace(/{{ remaining_amount }}/g, `<span class="fsp-amount-needed">${this.formatMoney(remaining)}</span>`)
      .replace(/{{ current_amount }}/g, this.formatMoney(current))
      .replace(/{{ threshold_amount }}/g, this.formatMoney(threshold))
      .replace(/{{ reward_name }}/g, rewardName);
  }

  renderMarkers() {
    if (!this.markersContainer || this.milestones.length === 0) return;
    this.markersContainer.innerHTML = '';
    
    const maxThreshold = this.milestones[this.milestones.length - 1].threshold;

    this.milestones.forEach((milestone, index) => {
      const percentage = Math.min((milestone.threshold / maxThreshold) * 100, 100);
      const marker = document.createElement('div');
      marker.className = 'fsp-milestone-marker';
      marker.style.left = `${percentage}%`;
      marker.setAttribute('data-index', index);
      
      marker.innerHTML = `
        <div class="fsp-milestone-icon">${milestone.icon || ''}</div>
        <div class="fsp-milestone-text">${milestone.reward_name}</div>
      `;
      this.markersContainer.appendChild(marker);
    });
  }

  updateProgress(cartTotal) {
    if (this.milestones.length === 0) return;
    
    const maxThreshold = this.milestones[this.milestones.length - 1].threshold;
    let percentage = Math.min((cartTotal / maxThreshold) * 100, 100);
    
    if (this.progressBar) {
      this.progressBar.style.width = `${percentage}%`;
    }

    let nextMilestone = this.milestones.find(m => m.threshold > cartTotal);
    let lastUnlocked = [...this.milestones].reverse().find(m => m.threshold <= cartTotal);

    if (this.markersContainer) {
      const markers = this.markersContainer.querySelectorAll('.fsp-milestone-marker');
      markers.forEach((marker, index) => {
        if (this.milestones[index].threshold <= cartTotal) {
          marker.classList.add('fsp-unlocked');
        } else {
          marker.classList.remove('fsp-unlocked');
        }
      });
    }

    if (this.messageEl) {
      if (nextMilestone) {
        this.progressBar?.classList.remove('fsp-success');
        const amountNeeded = nextMilestone.threshold - cartTotal;
        
        let msg = nextMilestone.progress_msg || `You're only {{ remaining_amount }} away from {{ reward_name }}!`;
        this.messageEl.innerHTML = this.parseMessage(msg, amountNeeded, cartTotal, nextMilestone.threshold, nextMilestone.reward_name);
      } else if (lastUnlocked) {
        this.progressBar?.classList.add('fsp-success');
        let msg = lastUnlocked.success_msg || `🎉 You've unlocked {{ reward_name }}!`;
        this.messageEl.innerHTML = this.parseMessage(msg, 0, cartTotal, lastUnlocked.threshold, lastUnlocked.reward_name);
      } else {
        this.messageEl.innerHTML = this.settings.initialMessage || 'Add items to unlock rewards!';
      }
    }
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
